import { useState, useEffect, useCallback, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Trash2, Search, Shield, ShieldOff, UserCheck, Eye, AppWindow } from 'lucide-react'
import {
  listUsers, createUser, updateUser, deleteUser,
  impersonateUser, listAllApps,
  type User, type AdminApp
} from '../../lib/api'
import { UserContext } from '../../hooks/useUser'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
type UserSort = 'recent' | 'name' | 'most-apps' | 'most-total-views' | 'most-viewed-app'

type UserAppStats = {
  appCount: number
  runningCount: number
  totalViews30d: number
  topAppViews30d: number
}

const emptyUserAppStats: UserAppStats = {
  appCount: 0,
  runningCount: 0,
  totalViews30d: 0,
  topAppViews30d: 0,
}

function getUserDisplayName(user: User): string {
  return user.first_name || user.last_name
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
    : user.name || user.email.split('@')[0]
}

export default function AdminUsers() {
  const navigate = useNavigate()
  const currentUser = useContext(UserContext)
  const [users, setUsers] = useState<User[]>([])
  const [apps, setApps] = useState<AdminApp[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [newUser, setNewUser] = useState({ email: '', name: '', is_admin: false })
  const [filter, setFilter] = useState<'all' | 'has_apps' | 'running_apps'>('all')
  const [sortBy, setSortBy] = useState<UserSort>('recent')

  const loadUsers = useCallback(async () => {
    try {
      const [usersData, appsData] = await Promise.all([
        listUsers(search || undefined),
        listAllApps().catch(() => []), // Silently fail if no apps
      ])
      setUsers(usersData)
      setApps(appsData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [search])

  const userAppStats = new Map<number, UserAppStats>()
  for (const app of apps) {
    const existing = userAppStats.get(app.user_id) ?? { ...emptyUserAppStats }
    const visitCount = app.visit_count_30d ?? 0

    existing.appCount += 1
    existing.runningCount += app.status === 'running' ? 1 : 0
    existing.totalViews30d += visitCount
    existing.topAppViews30d = Math.max(existing.topAppViews30d, visitCount)

    userAppStats.set(app.user_id, existing)
  }

  function getUserStats(userId: number): UserAppStats {
    return userAppStats.get(userId) ?? emptyUserAppStats
  }

  const usersWithApps = users.filter(user => getUserStats(user.id).appCount > 0).length
  const usersWithRunningApps = users.filter(user =>
    getUserStats(user.id).runningCount > 0
  ).length

  const filteredUsers = users
    .filter(user => {
      const userStats = getUserStats(user.id)
      if (filter === 'has_apps') return userStats.appCount > 0
      if (filter === 'running_apps') return userStats.runningCount > 0
      return true
    })
    .sort((left, right) => {
      const leftStats = getUserStats(left.id)
      const rightStats = getUserStats(right.id)

      if (sortBy === 'name') {
        return getUserDisplayName(left).localeCompare(getUserDisplayName(right))
      }

      if (sortBy === 'most-apps') {
        return (
          rightStats.appCount - leftStats.appCount
          || rightStats.totalViews30d - leftStats.totalViews30d
          || getUserDisplayName(left).localeCompare(getUserDisplayName(right))
        )
      }

      if (sortBy === 'most-total-views') {
        return (
          rightStats.totalViews30d - leftStats.totalViews30d
          || rightStats.topAppViews30d - leftStats.topAppViews30d
          || getUserDisplayName(left).localeCompare(getUserDisplayName(right))
        )
      }

      if (sortBy === 'most-viewed-app') {
        return (
          rightStats.topAppViews30d - leftStats.topAppViews30d
          || rightStats.totalViews30d - leftStats.totalViews30d
          || getUserDisplayName(left).localeCompare(getUserDisplayName(right))
        )
      }

      return (
        new Date(right.created_at).getTime() - new Date(left.created_at).getTime()
        || getUserDisplayName(left).localeCompare(getUserDisplayName(right))
      )
    })

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  useEffect(() => {
    const timer = setTimeout(() => {
      loadUsers()
    }, 300)
    return () => clearTimeout(timer)
  }, [loadUsers])

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    setCreating(true)
    setError(null)

    try {
      await createUser(newUser)
      await loadUsers()
      setShowCreate(false)
      setNewUser({ email: '', name: '', is_admin: false })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user')
    } finally {
      setCreating(false)
    }
  }

  async function handleToggleAdmin(user: User) {
    try {
      await updateUser(user.id, { is_admin: !user.is_admin })
      await loadUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user')
    }
  }

  async function handleToggleActive(user: User) {
    try {
      await updateUser(user.id, { is_active: !user.is_active })
      await loadUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user')
    }
  }

  async function handleDelete(id: number) {
    try {
      await deleteUser(id)
      await loadUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user')
    }
  }

  async function handleImpersonate(userId: number) {
    setActionLoading(`impersonate-${userId}`)
    setError(null)
    try {
      await impersonateUser(userId)
      // impersonateUser redirects to / after completion
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to impersonate user')
      setActionLoading(null)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold mb-2">Users</h1>
          <p className="text-muted-foreground">Manage platform users and permissions</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add User
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      {/* Search and Filter */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-2 rounded-lg text-sm transition-colors ${
              filter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'
            }`}
          >
            All ({users.length})
          </button>
          <button
            onClick={() => setFilter('has_apps')}
            className={`px-3 py-2 rounded-lg text-sm transition-colors ${
              filter === 'has_apps' ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'
            }`}
          >
            Has Apps ({usersWithApps})
          </button>
          <button
            onClick={() => setFilter('running_apps')}
            className={`px-3 py-2 rounded-lg text-sm transition-colors ${
              filter === 'running_apps' ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'
            }`}
          >
            Running ({usersWithRunningApps})
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Sort</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as UserSort)}
            className="px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="recent">Newest</option>
            <option value="most-viewed-app">Highest Single-App Views (30d)</option>
            <option value="most-total-views">Most Total App Views (30d)</option>
            <option value="most-apps">Most Apps</option>
            <option value="name">Name A-Z</option>
          </select>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Add User</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  required
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Name (optional)</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="isAdmin"
                  checked={newUser.is_admin}
                  onChange={(e) => setNewUser({ ...newUser, is_admin: e.target.checked })}
                  className="w-4 h-4 rounded border-border"
                />
                <label htmlFor="isAdmin" className="text-sm">
                  Admin privileges
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !newUser.email}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {creating ? 'Adding...' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Users Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
        {/* Mobile: card list. Stacked layout — keeps text readable on 375px. */}
        <div className="md:hidden space-y-3">
          {filteredUsers.map((user) => {
            const userStats = getUserStats(user.id)
            const displayName = getUserDisplayName(user)
            return (
              <div
                key={`m-${user.id}`}
                className="border border-border rounded-xl p-4 bg-card cursor-pointer hover:bg-secondary/30"
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('button')) return
                  navigate(`/admin/users/${user.id}`)
                }}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{displayName}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleToggleActive(user)}
                      className={`px-2 py-0.5 rounded text-xs ${
                        user.is_active ? 'bg-green-500/20 text-green-500' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {user.is_active ? 'Active' : 'Inactive'}
                    </button>
                    <button
                      onClick={() => handleToggleAdmin(user)}
                      className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                        user.is_admin ? 'bg-amber-500/20 text-amber-500' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {user.is_admin ? <Shield className="w-3 h-3" /> : <ShieldOff className="w-3 h-3" />}
                      {user.is_admin ? 'Admin' : 'User'}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                  <span>
                    {userStats.appCount === 0
                      ? 'No apps'
                      : `${userStats.appCount} apps · ${userStats.runningCount} running`}
                  </span>
                  <span>
                    {userStats.appCount === 0
                      ? '—'
                      : `${userStats.totalViews30d.toLocaleString()} views (30d)`}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    Last login{' '}
                    {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'never'}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      title="View profile"
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                      className="p-2 rounded-lg hover:bg-secondary transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {currentUser?.email !== user.email && (
                      <ConfirmDialog
                        trigger={
                          <button
                            title="Impersonate"
                            disabled={actionLoading === `impersonate-${user.id}`}
                            className="p-2 rounded-lg hover:bg-blue-500/10 hover:text-blue-500 transition-colors disabled:opacity-50"
                          >
                            <UserCheck className={`w-4 h-4 ${actionLoading === `impersonate-${user.id}` ? 'animate-pulse' : ''}`} />
                          </button>
                        }
                        title="Impersonate User"
                        description={`Start impersonating ${user.first_name || user.name || user.email}?`}
                        confirmLabel="Impersonate"
                        onConfirm={() => handleImpersonate(user.id)}
                      />
                    )}
                    <ConfirmDialog
                      trigger={
                        <button title="Delete" className="p-2 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      }
                      title="Delete User"
                      description={`Are you sure you want to delete ${user.first_name || user.name || user.email}?`}
                      confirmLabel="Delete"
                      variant="destructive"
                      onConfirm={() => handleDelete(user.id)}
                    />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Desktop: table */}
        <div className="hidden md:block rounded-xl border border-border overflow-x-auto">
          <table className="w-full min-w-[840px]">
            <thead className="bg-secondary">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium">User</th>
                <th className="text-left px-4 py-3 text-sm font-medium">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium">Role</th>
                <th className="text-left px-4 py-3 text-sm font-medium">Apps</th>
                <th className="text-right px-4 py-3 text-sm font-medium">Views (30d)</th>
                <th className="text-left px-4 py-3 text-sm font-medium">Last Login</th>
                <th className="text-right px-4 py-3 text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredUsers.map((user) => {
                const userStats = getUserStats(user.id)
                const displayName = getUserDisplayName(user)

                return (
                  <tr
                    key={user.id}
                    className="hover:bg-secondary/50 cursor-pointer"
                    onClick={(e) => {
                      // Don't navigate if clicking on a button
                      if ((e.target as HTMLElement).closest('button')) return
                      navigate(`/admin/users/${user.id}`)
                    }}
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{displayName}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleActive(user)}
                        className={`px-2 py-1 rounded text-xs ${
                          user.is_active
                            ? 'bg-green-500/20 text-green-500'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {user.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleAdmin(user)}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                          user.is_admin
                            ? 'bg-amber-500/20 text-amber-500'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {user.is_admin ? (
                          <>
                            <Shield className="w-3 h-3" /> Admin
                          </>
                        ) : (
                          <>
                            <ShieldOff className="w-3 h-3" /> User
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      {userStats.appCount === 0 ? (
                        <span className="text-muted-foreground text-sm">—</span>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <AppWindow className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-sm">{userStats.appCount}</span>
                          {userStats.runningCount > 0 && (
                            <span className="text-xs text-green-500">({userStats.runningCount} running)</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {userStats.appCount === 0 ? (
                        <span className="text-muted-foreground text-sm">—</span>
                      ) : (
                        <div>
                          <p className="text-sm font-medium">{userStats.totalViews30d.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">
                            top app {userStats.topAppViews30d.toLocaleString()}
                          </p>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {user.last_login
                        ? new Date(user.last_login).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {/* View details button */}
                        
                          <button title="View full user profile and activity"
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            className="p-2 rounded-lg hover:bg-secondary transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        
                        {/* Impersonate button - don't show for self */}
                        {currentUser?.email !== user.email && (
                          <ConfirmDialog
                            trigger={
                              
                                <button title="Sign in as this user to see their view of the platform"
                                  disabled={actionLoading === `impersonate-${user.id}`}
                                  className="p-2 rounded-lg hover:bg-blue-500/10 hover:text-blue-500 transition-colors disabled:opacity-50"
                                >
                                  <UserCheck className={`w-4 h-4 ${actionLoading === `impersonate-${user.id}` ? 'animate-pulse' : ''}`} />
                                </button>
                              
                            }
                            title="Impersonate User"
                            description={`Start impersonating ${user.first_name || user.name || user.email}? You will be logged in as them.`}
                            confirmLabel="Impersonate"
                            onConfirm={() => handleImpersonate(user.id)}
                          />
                        )}
                        <ConfirmDialog
                          trigger={
                            
                              <button title="Permanently remove this user and their data"
                                className="p-2 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            
                          }
                          title="Delete User"
                          description={`Are you sure you want to delete ${user.first_name || user.name || user.email}? This cannot be undone.`}
                          confirmLabel="Delete"
                          variant="destructive"
                          onConfirm={() => handleDelete(user.id)}
                        />
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  )
}
